/**
 * 연결이 끊긴 채팅 턴 되찾기.
 *
 * 로그인 턴은 화면이 떠나도(모바일 화면 꺼짐·앱 전환·네트워크 전환) 서버가 끝까지 만들어
 * chat_turn_results 에 남긴다. 화면은 GET /api/chat/turns/{id} 로 그 결과를 받아
 * 원래 받은 것처럼 onComplete 로 넘긴다 — 같은 답을 다시 만들지 않는다.
 */

const FAST_POLL_MS = 2000;
const SLOW_POLL_MS = 4000;
const FAST_WINDOW_MS = 30_000;
export const RECOVERY_LIMIT_MS = 10 * 60_000;
// running 기록은 서버 준비(인증·자료 읽기, 측정 3~4초) 뒤 비동기로 쓰인다.
// 그 사이·쓰기가 느릴 때 unknown 으로 보고 다시 보내면 같은 답을 두 번 만들므로 16초 넘게 참는다.
export const MAX_UNKNOWN = 8;

export const newChatTurnId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }
  const random = () => Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}${random()}${random()}`;
};

/**
 * 이 턴의 메시지 문서 id. 화면이 떠난 채 답이 끝나면 서버가 같은 id 로 대신 저장한다
 * (backend chat_orphan_turn_saver.message_doc_ids). 둘이 같아야 나중에 화면이 저장해도 두 번 남지 않는다.
 */
export const turnMessageDocIds = (turnId) => (
  /^[A-Za-z0-9_-]{8,64}$/.test(String(turnId || ''))
    ? { user: `turn_${turnId}_user`, assistant: `turn_${turnId}_ai` }
    : null
);

/** 다음 조회까지 기다린다. 화면이 다시 보이거나 인터넷이 돌아오면 바로 깨운다. */
const waitForNextPoll = (delayMs, signal) => new Promise((resolve) => {
  let timer = null;
  const finish = () => {
    clearTimeout(timer);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
    if (typeof window !== 'undefined') window.removeEventListener('online', finish);
    signal?.removeEventListener?.('abort', finish);
    resolve();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') finish();
  };
  timer = setTimeout(finish, delayMs);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  if (typeof window !== 'undefined') window.addEventListener('online', finish);
  signal?.addEventListener?.('abort', finish);
});

/**
 * @returns {Promise<{status: 'completed'|'failed'|'unknown'|'timeout'|'aborted', complete?: object, error_code?: string, error?: string, retryable?: boolean}>}
 */
export const recoverChatTurn = async ({
  baseUrl,
  turnId,
  getToken,
  signal = null,
  limitMs = RECOVERY_LIMIT_MS,
  maxUnknown = MAX_UNKNOWN,
  now = () => Date.now(),
  wait = waitForNextPoll,
}) => {
  const started = now();
  let unknownCount = 0;
  // 제한 시간은 조회 뒤에 본다 — 백그라운드에서 오래 멈췄다 깨어나도 한 번은 확인하고 끝낸다.
  for (;;) {
    if (signal?.aborted) return { status: 'aborted' };
    let body = null;
    try {
      const token = await getToken();
      const response = await fetch(`${baseUrl}/api/chat/turns/${encodeURIComponent(turnId)}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'omit',
        mode: 'cors',
        ...(signal ? { signal } : {}),
      });
      // 404: 이 기능이 없는 이전 서버(배포 중) · 400/401: 되찾을 수 없는 요청
      if ([400, 401, 403, 404].includes(response.status)) return { status: 'unknown' };
      if (response.ok) body = await response.json();
    } catch (error) {
      if (error?.name === 'AbortError' || signal?.aborted) return { status: 'aborted' };
      // 아직 인터넷이 안 돌아왔다 — 계속 기다린다
    }
    if (body?.status === 'completed' && body.complete && typeof body.complete === 'object') return body;
    if (body?.status === 'failed') return body;
    if (body?.status === 'unknown') {
      unknownCount += 1;
      if (unknownCount >= maxUnknown) return body;
    }
    const elapsed = now() - started;
    if (elapsed >= limitMs) return { status: 'timeout' };
    await wait(elapsed < FAST_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS, signal);
  }
};

/** 사용자가 '멈춤'을 눌렀다 — 연결이 끊긴 뒤에도 도는 서버 생성을 멈춘다. 실패해도 화면 흐름은 막지 않는다. */
export const cancelChatTurn = async ({ baseUrl, turnId, getToken, keepPartial = false }) => {
  if (!turnId || !getToken) return;
  try {
    // 되찾기가 길어지면 요청 시작 때 받은 토큰은 만료됐을 수 있다
    const token = await getToken();
    if (!token) return;
    // keep=1: 화면이 받은 만큼을 남겼다 — 서버도 같은 문서 id 로 확인 저장한다. 없으면 화면이 턴을 지웠으니 서버도 남기지 않는다.
    const query = keepPartial ? '?keep=1' : '';
    await fetch(`${baseUrl}/api/chat/turns/${encodeURIComponent(turnId)}/cancel${query}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
      mode: 'cors',
      keepalive: true,
    });
  } catch (_) {
    // 화면 흐름과 무관
  }
};
